import {
  contactSupportReasons,
  displayErrorMessageModes,
} from '../../components/GlobalLoadingModal/GlobalLoadingModal'

type ContactSupportReason = (typeof contactSupportReasons)[keyof typeof contactSupportReasons]
type DisplayErrorMessageMode =
  (typeof displayErrorMessageModes)[keyof typeof displayErrorMessageModes]
type SetLoadingModalConfigProps = {
  open: boolean
  title?: string
  dialogMessage?: string
  successfulMessage?: string
  error?: Error
  errorMessage?: string
  displayErrorMessageMode?: DisplayErrorMessageMode
  dryRun?: boolean
}

export type ProcessLoadingProps = {
  setLoadingModalConfig: (props: SetLoadingModalConfigProps) => void
  doAction: (props: { dryRun: boolean }) => Promise<void>
  loadingTitle?: string
  loadingMessage?: string
  successfulMessage?: string
  contactReasonSupportType?: ContactSupportReason
  errorMessage?: string
  displayErrorMessageMode?: DisplayErrorMessageMode
  dryRun?: boolean
  displayErrorCustomMessage?: boolean
}

export async function processLoading({
  setLoadingModalConfig,
  doAction,
  loadingTitle,
  loadingMessage = 'Guardando información...',
  successfulMessage = 'Se modificó la entidad correctamente',
  contactReasonSupportType = contactSupportReasons.Generic,
  errorMessage = 'Error al guardar la entidad, por favor intente nuevamente',
  displayErrorMessageMode = displayErrorMessageModes.snackbar,
  dryRun = false,
  displayErrorCustomMessage = true, // There are some cases where we want to
  // display contact message error given and others where we want to display a title message error.
  // For example when we have a validation error we want to display the error message given by the errorMessage parameter
  // but when we have an unexpected error we want to display the title error to avoid to show internal errors to the user.
}: ProcessLoadingProps): Promise<void> {
  try {
    setLoadingModalConfig({
      open: true,
      title: loadingTitle,
      dialogMessage: loadingMessage,
    })

    await doAction({ dryRun })

    setLoadingModalConfig({
      open: false,
      successfulMessage,
      dryRun,
    })
  } catch (error) {
    const newErrorMessage = displayErrorCustomMessage
      ? errorMessage
      : contactReasonSupportType.title

    setLoadingModalConfig({
      open: false,
      title: contactReasonSupportType.title,
      dialogMessage: contactReasonSupportType.detail,
      error: error as Error,
      errorMessage: newErrorMessage,
      displayErrorMessageMode,
    })
  }
}

export async function processLoadingWithConfirmation({
  setPartialChanges,
  loadingModalConfig,
  doAction,
  confirmationModalConfig,
  displayErrorCustomMessage,
}): Promise<void> {
  await processLoading({
    setLoadingModalConfig: loadingModalConfig.setLoadingModalConfig,
    loadingTitle: loadingModalConfig.loadingTitle,
    loadingMessage: loadingModalConfig.loadingMessage,
    successfulMessage: loadingModalConfig.successfulMessage,
    contactReasonSupportType: loadingModalConfig.contactReasonSupportType,
    errorMessage: loadingModalConfig.errorMessage,
    displayErrorMessageMode: loadingModalConfig.displayErrorMessageMode,
    dryRun: true,
    displayErrorCustomMessage,
    doAction: async ({ dryRun }) => {
      await doAction({ dryRun })

      confirmationModalConfig.setConfirmationModalConfig({
        title: confirmationModalConfig.title,
        message: confirmationModalConfig.message,
        open: true,
        confirmAction: async () => {
          confirmationModalConfig.setConfirmationModalConfig({ open: false })

          await processLoading({
            setLoadingModalConfig: loadingModalConfig.setLoadingModalConfig,
            loadingTitle: loadingModalConfig.loadingTitle,
            loadingMessage: loadingModalConfig.loadingMessage,
            successfulMessage: loadingModalConfig.successfulMessage,
            contactReasonSupportType: loadingModalConfig.contactReasonSupportType,
            errorMessage: loadingModalConfig.errorMessage,
            displayErrorMessageMode: loadingModalConfig.displayErrorMessageMode,
            doAction,
            dryRun: false,
          })
          setPartialChanges(false)
        },
      })
    },
  })
}
